import {
  BridgeDeviceFeatureStatus,
  BridgeDeviceStatus,
  IBridgeDeviceFeature,
  IPrincipleBridgeSettings,
} from '@principle-theorem/principle-core/interfaces';
import { IProvider, Firestore, TypeGuard } from '@principle-theorem/shared';
import { BridgeToCloudCommand } from './integrations/commands';
import { IBridgeIntegration, IDeviceCommand } from './send-command-to-device';
import { EZDENT_INTEGRATION } from './integrations/ezdent/ezdent';
import { HICAPS_CONNECT_INTEGRATION } from './integrations/hicaps-connect/hicaps-connect';
import { ROMEXIS_INTEGRATION } from './integrations/romexis/romexis';
import { SIDEXIS_INTEGRATION } from './integrations/sidexis/sidexis';
import { TWAIN_INTEGRATION } from './integrations/twain/twain';
import { VIXWIN_INTEGRATION } from './integrations/vixwin/vixwin';
import { CLINIVIEW_INTEGRATION } from './integrations/cliniview/cliniview';
import { IDIXEL_INTEGRATION } from './integrations/idixel/idixel';
import { VISTASOFT_INTEGRATION } from './integrations/vistasoft/vistasoft';
import { compact, isBoolean, isUndefined } from 'lodash';
import { DTX_STUDIO_INTEGRATION } from './integrations/dexis/dtx-studio';
import { DTX_STUDIO_DRIVER_DIRECT_INTEGRATION } from './integrations/dexis/dtx-studio-driver-direct';
import { DENTALEYE_INTEGRATION } from './integrations/dentaleye';
import { ROMEXIS_V2_INTEGRATION } from './integrations/romexis-v2/romexis-v2';

export type PrincipleSettingsUpdate = Omit<
  IPrincipleBridgeSettings,
  'version' | 'deviceId' | 'integrationKey'
>;

export const DEVICE_SETTINGS_PATH = '/settings';

const isOptionalFlag = TypeGuard.undefinedOr(isBoolean);
const isIntegrationSettings = TypeGuard.undefinedOr(
  TypeGuard.interface<{ enabled?: boolean }>({ enabled: isOptionalFlag })
);

export const isPrincipleSettingsUpdate = TypeGuard.interface<
  Partial<PrincipleSettingsUpdate>
>({
  sidexis: isIntegrationSettings,
  ezdent: isIntegrationSettings,
  twain: isIntegrationSettings,
  romexis: isIntegrationSettings,
  romexisV2: isIntegrationSettings,
  hicapsConnect: isIntegrationSettings,
  vixWin: isIntegrationSettings,
  cliniview: isIntegrationSettings,
  iDixel: isIntegrationSettings,
  vistaSoft: isIntegrationSettings,
  dtxStudio: isIntegrationSettings,
  dtxStudioDriverDirect: isIntegrationSettings,
  dentalEye: isIntegrationSettings,
  preventAutoUpdates: isOptionalFlag,
  betaAccess: isOptionalFlag,
});

export interface ISqlTestConnectionData {
  sqlServer: string;
  sqlPort: string;
  sqlDatabase: string;
  sqlUser: string;
  sqlPassword: string;
}

export interface ISqlTestConnectionResult {
  success: boolean;
  message: string;
}

export interface IDentalEyeTestConnectionData {
  sqlServer: string;
  sqlPort: string;
  sqlDatabase: string;
  sqlUser: string;
  sqlPassword: string;
}

export interface IDentalEyeTestConnectionResult {
  success: boolean;
  message: string;
}

export interface IDentalEyeCreateSqlUserData {
  sqlServer: string;
  sqlPort: string;
  sqlDatabase: string;
}

export interface IDentalEyeCreateSqlUserResult {
  success: boolean;
  message: string;
}

export interface ISidexisDiscoveredSettings {
  pdataPath: string;
  appPath: string;
  stationName: string;
  sidexisMailboxPath: string;
  principleMailboxPath: string;
}

export interface ISidexisAutoConfigResult {
  success: boolean;
  settings: ISidexisDiscoveredSettings;
  warnings: string[];
}

export type ISidexisAutoConfigDisplayResult = Pick<
  ISidexisAutoConfigResult,
  'success' | 'warnings'
>;

export interface IPrincipleMethods {
  setSettings: (settings: Partial<IPrincipleBridgeSettings>) => Promise<void>;
  getSettings: () => Promise<IPrincipleBridgeSettings>;
  getVersion: () => Promise<string>;
  openLog: () => Promise<string>;
  statusChange: (listener: (status: BridgeDeviceStatus) => void) => void;
  settingsChange: (
    listener: (settings: IPrincipleBridgeSettings) => void
  ) => void;
  openDirectoryPicker: () => Promise<string[]>;
  openFilePicker: () => Promise<string[]>;
  reconnect: () => Promise<void>;
  copyResourceToClipboard: (relativePath: string) => Promise<void>;
  testDentalEyeConnection: (
    params: IDentalEyeTestConnectionData
  ) => Promise<IDentalEyeTestConnectionResult>;
  createDentalEyeSqlUser: (
    params: IDentalEyeCreateSqlUserData
  ) => Promise<IDentalEyeCreateSqlUserResult>;
  testRomexisV2Connection: (
    params: ISqlTestConnectionData
  ) => Promise<ISqlTestConnectionResult>;
  autoConfigureSidexis: (
    pdataPath?: string
  ) => Promise<ISidexisAutoConfigResult>;
}

export class SettingsCommandProvider
  implements IProvider<IDeviceCommand, Promise<void>>
{
  canProvide(data: IDeviceCommand): boolean {
    return data.command.type === String(BridgeToCloudCommand.Settings);
  }

  async execute(data: IDeviceCommand): Promise<void> {
    const settings = data.command.data as IPrincipleBridgeSettings;
    const features: IBridgeDeviceFeature[] = compact([
      this._getFeature(SIDEXIS_INTEGRATION, settings.sidexis?.enabled),
      this._getFeature(EZDENT_INTEGRATION, settings.ezdent?.enabled),
      this._getFeature(TWAIN_INTEGRATION, settings.twain?.enabled),
      this._getFeature(ROMEXIS_INTEGRATION, settings.romexis?.enabled),
      this._getFeature(ROMEXIS_V2_INTEGRATION, settings.romexisV2?.enabled),
      this._getFeature(
        HICAPS_CONNECT_INTEGRATION,
        settings.hicapsConnect?.enabled
      ),
      this._getFeature(VIXWIN_INTEGRATION, settings.vixWin?.enabled),
      this._getFeature(CLINIVIEW_INTEGRATION, settings.cliniview?.enabled),
      this._getFeature(IDIXEL_INTEGRATION, settings.iDixel?.enabled),
      this._getFeature(VISTASOFT_INTEGRATION, settings.vistaSoft?.enabled),
      this._getFeature(DTX_STUDIO_INTEGRATION, settings.dtxStudio?.enabled),
      this._getFeature(
        DTX_STUDIO_DRIVER_DIRECT_INTEGRATION,
        settings.dtxStudioDriverDirect?.enabled
      ),
      this._getFeature(DENTALEYE_INTEGRATION, settings.dentalEye?.enabled),
    ]);

    await Firestore.patchDoc(data.deviceRef, {
      features,
      settings,
    });
  }

  private _getFeature(
    integration: IBridgeIntegration,
    enabled?: boolean
  ): IBridgeDeviceFeature | undefined {
    if (isUndefined(enabled)) {
      return;
    }
    return {
      ...integration,
      status: enabled
        ? BridgeDeviceFeatureStatus.Enabled
        : BridgeDeviceFeatureStatus.Disabled,
    };
  }
}
